import { listGroupNavigationHashTooltip } from './getPersonaListEntries.locstring.json';
import loc from 'owa-localize';
/* tslint:disable:jsx-no-lambda WI:47582 */
import * as React from 'react';
import { createSelectorCreator, defaultMemoize } from 'reselect';

import type { PersonaListEntry, PersonaEntry } from '../../../selectors/personaListSelector';
import PersonaItemAsync from '../personaItem/personaItemAsync';

import type { DataProps, DispatchProps, CheckPersonaListItemToggleType } from './personaList';
import {
    HubPersona,
    deletedItemsLeftNavItemId,
    isInFavorites,
    favoritesLeftNavItemId,
    HubListEntryKind,
    HubListInterface,
    FavoriteEntity,
    isHubPersonaMultiselectFriendly,
} from '../../../../shared';
import type { PersonaClickedOrigin } from '../../../actions/user/personaListActions';
import { Icon } from '@fluentui/react/lib/Icon';
import { shallowCompare } from '@fluentui/utilities/lib/object';
import type { PersonaListViewModel } from '../../../models/PersonaListViewModel';
import { isFeatureEnabled } from 'owa-feature-flags';

import type {
    HubListContentItem,
    HubListItem,
    SparseHubListItems,
} from '../../../../shared/components/hubList';
import { calculateMultiCheckChanges, calculateSingleCheckChanges } from './multiCheckHelper';
import { NUMBER_ONLY_INITIAL } from '../../../utils/initial';
import type { SearchResultType } from '../../../actions/user/resultTypeLabelActions';
import FavoritePickerContainerAsync from '../../favoritePickerContainer/favoritePickerContainerAsync';
import type { SearchResultViewModel } from '../personaItem/personaItem';
import { isSortingWithInitials } from '../../../utils/personaListSortUtils';

const CONTENT_ITEM_HEIGHT = 60;
const GROUP_ITEM_HEIGHT = 40;
const FAVORITE_PICKER_ITEM_HEIGHT = 40;

import styles from './personaList.scss';

type Props = DataProps &
    DispatchProps & {
        readonly onFocusDebounceHandler: (func: () => void) => void;
    };

type SelectedHubPersonasState = {
    currentCheckedHubPersonaIds: string[];
    clickedHubPersonaId: string;
};

const getSelectedHubPersonasState = (props: Props): SelectedHubPersonasState => {
    return {
        currentCheckedHubPersonaIds: props.currentCheckedHubPersonaIds,
        clickedHubPersonaId: props.clickedHubPersonaId,
    };
};

function shallowCompareFunction<T extends {}>(left: T, right: T, index: number): boolean {
    if (left && right) {
        return shallowCompare(left, right);
    }

    return left === right;
}

export const getPersonaListEntries = createSelectorCreator(defaultMemoize, shallowCompareFunction)<
    Props,
    Props,
    SelectedHubPersonasState,
    HubListItem[]
>([props => props, getSelectedHubPersonasState], (props, selectedHubPersonasState) => {
    const itemClickOrigin = props.isSearchMode ? 'personaListSearch' : 'personaList';

    const entries: HubListItem[] = props.entries.map(entry => {
        if (entry.kind === HubListEntryKind.Content) {
            return {
                ...entry,
                id: entry.value.hubPersonaId,
                height: CONTENT_ITEM_HEIGHT,
                render: (hubList: HubListInterface, index: number) =>
                    renderPersonaCell(
                        entry,
                        index,
                        hubList,
                        itemClickOrigin,
                        props.viewState,
                        props.isInCheckMode,
                        props.isInPublicFolder,
                        props.searchResultViewModel,
                        selectedHubPersonasState,
                        props.activeLeftNavItemId,
                        props.entries,
                        props.favoritePersons,
                        props.onPersonaFocus,
                        props.onFocusDebounceHandler,
                        props.onPersonaClick,
                        props.onCheckPersonaListItemToggle,
                        props.onSearchResultTypeLabelClicked,
                        props.isSearchWithPersonaPill
                    ),
                onSpacePressed: isShiftKeyPressed => {
                    const isMultiselectable =
                        props.activeLeftNavItemId !== favoritesLeftNavItemId &&
                        !props.isInPublicFolder;
                    !props.isSearchWithPersonaPill;
                    if (!isMultiselectable) {
                        props.onPersonaClick(entry.value, itemClickOrigin);
                    } else {
                        const onSpacePressed = isShiftKeyPressed
                            ? onPersonaShiftClick
                            : onCheckPersonaToggleClick;
                        onSpacePressed(
                            entry.value,
                            props.entries,
                            selectedHubPersonasState,
                            props.activeLeftNavItemId,
                            props.onCheckPersonaListItemToggle
                        );
                    }
                },
            };
        }
        const groupContainer = isSortingWithInitials(props.viewState.sorting)
            ? styles.initial
            : styles.name;

        return {
            ...entry,
            id: entry.index,
            title:
                entry.index === NUMBER_ONLY_INITIAL
                    ? loc(listGroupNavigationHashTooltip)
                    : undefined,
            height: GROUP_ITEM_HEIGHT,
            element: (
                <div className={groupContainer}>
                    {entry.iconName ? (
                        <Icon iconName={entry.iconName} className={styles.icon} />
                    ) : (
                        entry.index
                    )}
                </div>
            ),
            centerOverPhoto: isSortingWithInitials(props.viewState.sorting),
        };
    });

    if (
        isFeatureEnabled('people-favoritePicker') &&
        props.activeLeftNavItemId === favoritesLeftNavItemId &&
        props.entries.length !== 0
    ) {
        const addFavoriteButton: HubListContentItem = {
            kind: HubListEntryKind.Content,
            render: () => (
                <div key={'favContainer'}>
                    <FavoritePickerContainerAsync
                        onFocusDebounceHandler={props.onFocusDebounceHandler}
                    />
                </div>
            ),
        };
        entries.push({
            ...addFavoriteButton,
            id: 'favorite-picker-id',
            height: FAVORITE_PICKER_ITEM_HEIGHT,
        });
    }

    return entries;
});

export const getSparsePersonaListEntries = createSelectorCreator(
    defaultMemoize,
    shallowCompareFunction
)<Props, Props, SelectedHubPersonasState, SparseHubListItems | undefined>(
    [props => props, getSelectedHubPersonasState],
    (props, selectedHubPersonasState) => {
        const itemClickOrigin = 'personaList';

        let entries: SparseHubListItems = undefined;

        Object.keys(props.sparseEntries).forEach(key => {
            const entry = props.sparseEntries[key];
            if (entries === undefined) {
                entries = {};
            }

            if (entry?.value) {
                entries[key] = {
                    ...entry,
                    id: entry.value.hubPersonaId,
                    height: CONTENT_ITEM_HEIGHT,
                    render: (hubList: HubListInterface, index: number) =>
                        renderPersonaCell(
                            entry,
                            index,
                            hubList,
                            itemClickOrigin,
                            props.viewState,
                            props.isInCheckMode,
                            props.isInPublicFolder,
                            props.searchResultViewModel,
                            selectedHubPersonasState,
                            props.activeLeftNavItemId,
                            props.entries,
                            props.favoritePersons,
                            props.onPersonaFocus,
                            props.onFocusDebounceHandler,
                            props.onPersonaClick,
                            props.onCheckPersonaListItemToggle,
                            props.onSearchResultTypeLabelClicked,
                            props.isSearchWithPersonaPill
                        ),
                    onSpacePressed: isShiftKeyPressed => {
                        const isMultiselectable =
                            props.activeLeftNavItemId !== favoritesLeftNavItemId &&
                            !props.isInPublicFolder;
                        !props.isSearchWithPersonaPill;
                        if (!isMultiselectable) {
                            props.onPersonaClick(entry.value, itemClickOrigin);
                        } else {
                            const onSpacePressed = isShiftKeyPressed
                                ? onPersonaShiftClick
                                : onCheckPersonaToggleClick;
                            onSpacePressed(
                                entry.value,
                                props.entries,
                                selectedHubPersonasState,
                                props.activeLeftNavItemId,
                                props.onCheckPersonaListItemToggle
                            );
                        }
                    },
                };
            }
        });

        return entries;
    }
);

function renderPersonaCell(
    entry: PersonaEntry,
    index: number,
    hubList: HubListInterface,
    itemClickOrigin: PersonaClickedOrigin,
    viewState: PersonaListViewModel,
    isInCheckMode: boolean,
    isInPublicFolder: boolean,
    searchResultViewModel: SearchResultViewModel,
    selectedHubPersonasState: SelectedHubPersonasState,
    activeLeftNavItemId: string,
    entries: PersonaListEntry[],
    favoritePersons: FavoriteEntity[],
    onPersonaFocus: (persona: HubPersona) => void,
    onFocusDebounceHandler: (func: () => void) => void,
    onPersonaClick: (persona: HubPersona, origin: PersonaClickedOrigin) => void,
    onCheckPersonaListItemToggle: CheckPersonaListItemToggleType,
    onResultTypeLabelClicked: (resultType: SearchResultType) => void,
    isSearchWithPersonaPill: boolean
): JSX.Element {
    const isActive = hubList.isEntryActive(entry.value.hubPersonaId);
    const isMultiselectable =
        activeLeftNavItemId !== favoritesLeftNavItemId &&
        !isInPublicFolder &&
        isHubPersonaMultiselectFriendly(entry.value) &&
        !isSearchWithPersonaPill;
    const isItemActive = isInCheckMode
        ? isHubPersonaMultiselectFriendly(entry.value) && isActive
        : isActive;
    const isMultiSelected: boolean =
        selectedHubPersonasState.currentCheckedHubPersonaIds.length > 1;

    return (
        <PersonaItemAsync
            searchResultViewModel={searchResultViewModel}
            element={entry}
            displayOrder={viewState.displayOrder}
            isItemActive={isItemActive}
            isItemChecked={
                selectedHubPersonasState.currentCheckedHubPersonaIds &&
                selectedHubPersonasState.currentCheckedHubPersonaIds.includes(
                    entry.value.hubPersonaId
                )
            }
            clickedHubPersonaId={selectedHubPersonasState.clickedHubPersonaId}
            isInCheckMode={isInCheckMode}
            isMultiSelected={isMultiSelected}
            onPersonaFocus={onPersonaFocus}
            onFocusDebounceHandler={onFocusDebounceHandler}
            onShowContextMenu={hubList.onShowContextMenu}
            onPersonaClick={(persona: HubPersona) => {
                onPersonaClick(persona, itemClickOrigin);
                hubList.setActiveItem(persona.hubPersonaId);
            }}
            onCheckPersonaToggle={
                isMultiselectable
                    ? (persona: HubPersona) => {
                          onCheckPersonaToggleClick(
                              persona,
                              entries,
                              selectedHubPersonasState,
                              activeLeftNavItemId,
                              onCheckPersonaListItemToggle
                          );
                          hubList.setActiveItem(persona.hubPersonaId);
                      }
                    : undefined
            }
            onResultTypeLabelClicked={(resultType: SearchResultType) =>
                onResultTypeLabelClicked(resultType)
            }
            onPersonaShiftClick={
                isMultiselectable
                    ? (persona: HubPersona) => {
                          onPersonaShiftClick(
                              persona,
                              entries,
                              selectedHubPersonasState,
                              activeLeftNavItemId,
                              onCheckPersonaListItemToggle
                          );
                          hubList.setActiveItem(persona.hubPersonaId);
                      }
                    : undefined
            }
            isFavorite={isInFavorites(entry.value, favoritePersons)}
            hideCheckBox={!isMultiselectable}
            skipLoadingPhoto={() => hubList.isOutsideVisibleView(index)}
        />
    );
}

function onCheckPersonaToggleClick(
    checkedPersona: HubPersona,
    entries: PersonaListEntry[],
    selectedHubPersonasState: SelectedHubPersonasState,
    activeLeftItemId: string,
    onCheckPersonaListItemToggle: CheckPersonaListItemToggleType
): void {
    const fetchMoreDetails = activeLeftItemId !== deletedItemsLeftNavItemId;
    const currentDisplayedPersonas = (entries.filter(
        entry => entry.kind === HubListEntryKind.Content
    ) as PersonaEntry[]).map(personaEntry => personaEntry.value);
    const checkedChanges = calculateSingleCheckChanges(
        selectedHubPersonasState.currentCheckedHubPersonaIds,
        checkedPersona.hubPersonaId
    );
    const personasToCheck = currentDisplayedPersonas
        .filter(persona => checkedChanges.toCheck.includes(persona.hubPersonaId))
        .filter(isHubPersonaMultiselectFriendly);
    const personasToUncheck = currentDisplayedPersonas.filter(persona =>
        checkedChanges.toUncheck.includes(persona.hubPersonaId)
    );
    onCheckPersonaListItemToggle(personasToCheck, personasToUncheck, fetchMoreDetails);
}

function onPersonaShiftClick(
    clickedHubPersona: HubPersona,
    entries: PersonaListEntry[],
    selectedHubPersonasState: SelectedHubPersonasState,
    activeLeftItemId: string,
    onCheckPersonaListItemToggle: CheckPersonaListItemToggleType
): void {
    const fetchMoreDetails = activeLeftItemId !== deletedItemsLeftNavItemId;
    const currentDisplayedPersonas = (entries.filter(
        entry => entry.kind === HubListEntryKind.Content
    ) as PersonaEntry[]).map(personaEntry => personaEntry.value);
    const currentDisplayedIds = currentDisplayedPersonas.map(persona => persona.hubPersonaId);
    const selectionStart = selectedHubPersonasState.currentCheckedHubPersonaIds.length
        ? selectedHubPersonasState.currentCheckedHubPersonaIds
        : [selectedHubPersonasState.clickedHubPersonaId];
    const checkedChanges = calculateMultiCheckChanges(
        selectionStart,
        currentDisplayedIds,
        clickedHubPersona.hubPersonaId
    );
    const personasToCheckFilter = (persona: HubPersona) => {
        if (!selectedHubPersonasState.currentCheckedHubPersonaIds.length) {
            return (
                persona.hubPersonaId === selectedHubPersonasState.clickedHubPersonaId ||
                checkedChanges.toCheck.includes(persona.hubPersonaId)
            );
        }
        return checkedChanges.toCheck.includes(persona.hubPersonaId);
    };
    const personasToCheck = currentDisplayedPersonas
        .filter(personasToCheckFilter)
        .filter(isHubPersonaMultiselectFriendly);
    const personasToUncheck = currentDisplayedPersonas.filter(persona =>
        checkedChanges.toUncheck.includes(persona.hubPersonaId)
    );
    onCheckPersonaListItemToggle(personasToCheck, personasToUncheck, fetchMoreDetails);
}
